import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { eur } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import ModalityBadge, { cartPaymentCategory } from "@/components/app/ModalityBadge";
import { Trash2, ShoppingCart, Users, ArrowLeft, ArrowRight, CheckCircle2, GraduationCap, Wallet, AlertTriangle, CreditCard } from "lucide-react";
import { isGeneralCourse, isSpecificCourse } from "@/lib/specificTraining";

const PROFESSIONAL_PROFILES = ["Ingegnere", "Architetto", "Geometra", "Perito Industriale"];

const emptyParticipant = () => ({
  first_name: "", last_name: "", fiscal_code: "", birth_date: "", birth_place: "",
  email: "", phone: "", company: "", role: "",
  requires_cfp: false, professional_profile: "", professional_province: "",
});

export default function Cart() {
  const { items, updateQty, removeItem, clear } = useCart();
  const { user } = useAuth();
  const partner = user?.partner || {};
  const [priceMap, setPriceMap] = useState({});
  useEffect(() => {
    api.get("/courses").then((r) => setPriceMap(Object.fromEntries(r.data.map((c) => [c.id, c])))).catch(() => {});
  }, []);
  const priceOf = (it) => {
    const c = priceMap[it.course_id];
    if (!c) return { list: it.unit_price || 0, final: it.unit_price || 0, pct: 0, std: null, custom: false };
    return { list: c.partner_price || 0, final: c.partner_price_final ?? c.partner_price ?? 0, pct: c.partner_discount_percent || 0,
      std: c.standard_price ?? c.public_price ?? null, custom: !!c.partner_custom_price };
  };
  const [step, setStep] = useState(0); // 0 = carrello, 1 = corsisti
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // slots: { course_id: [participant, participant, ...] } based on quantity
  const [slots, setSlots] = useState({});
  // addons: { `${course_id}-${idx}`: specific_course_id } formazione specifica abbinata al corsista
  const [addons, setAddons] = useState({});
  const specificCourses = useMemo(() => Object.values(priceMap).filter(isSpecificCourse)
    .sort((a, b) => a.title.localeCompare(b.title)), [priceMap]);
  const addonQty = useMemo(() => {
    const m = {};
    Object.values(addons).forEach((cid) => { if (cid && priceMap[cid]) m[cid] = (m[cid] || 0) + 1; });
    return m;
  }, [addons, priceMap]);
  const effItems = useMemo(() => {
    const list = items.map((it) => ({ ...it, quantity: it.quantity + (addonQty[it.course_id] || 0) }));
    Object.entries(addonQty).forEach(([cid, q]) => {
      if (items.some((it) => it.course_id === cid)) return;
      const c = priceMap[cid];
      list.push({ course_id: cid, title: c.title, modality: c.modality, vat_rate: c.vat_rate, unit_price: c.partner_price, quantity: q });
    });
    return list;
  }, [items, addonQty, priceMap]);
  const [credit, setCredit] = useState(null);
  const [useCredit, setUseCredit] = useState(false);
  const [creditAmount, setCreditAmount] = useState(0);
  useEffect(() => {
    api.get("/partners/me/credit").then((r) => setCredit(r.data)).catch(() => setCredit(null));
  }, []);

  const totalSlots = useMemo(() => effItems.reduce((s, i) => s + i.quantity, 0), [effItems]);

  const pricingPreview = useMemo(() => {
    const breakdown = {};
    let originalNet = 0;
    let standardNet = 0;
    const vatExempt = cartPaymentCategory(effItems) === "elearning";
    effItems.forEach((it) => {
      const rate = vatExempt ? 0 : (typeof it.vat_rate === "number" ? it.vat_rate : 22);
      const pr = priceOf(it);
      const unitFinal = pr.final;
      const net = unitFinal * it.quantity;
      originalNet += pr.list * it.quantity;
      if (pr.std !== null) standardNet += pr.std * it.quantity;
      if (!breakdown[rate]) breakdown[rate] = { rate, net: 0, vat: 0 };
      breakdown[rate].net += net;
      breakdown[rate].vat += net * rate / 100;
    });
    const subtotal = Object.values(breakdown).reduce((s, b) => s + b.net, 0);
    const vat = Object.values(breakdown).reduce((s, b) => s + b.vat, 0);
    const r2 = (v) => Math.round(v * 100) / 100;
    return {
      subtotalOriginal: r2(originalNet),
      standardNet: r2(standardNet),
      saving: r2(Math.max(0, standardNet - subtotal)),
      discountAmount: r2(originalNet - subtotal),
      subtotal: r2(subtotal),
      vat: r2(vat),
      total: r2(subtotal + vat),
      breakdown: Object.values(breakdown).sort((a, b) => a.rate - b.rate),
      vatExempt,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effItems, priceMap]);

  const creditBalance = Number(credit?.credit_balance || 0);
  const creditInitial = Number(credit?.credit_initial || 0);
  const cartCategory = useMemo(() => cartPaymentCategory(effItems), [effItems]);
  const isElearningOrder = cartCategory === "elearning";
  const isClassroomOrder = cartCategory === "classroom";
  // Nei corsi E-learning il credito è obbligatorio e pari al totale.
  // Nei corsi Classroom il credito è vietato.
  const applicableCredit = Math.min(creditBalance, pricingPreview.total);
  const chosenCredit = isElearningOrder
    ? pricingPreview.total
    : (useCredit && !isClassroomOrder ? Math.max(0, Math.min(applicableCredit, parseFloat(creditAmount) || 0)) : 0);
  const residualToPay = Math.max(0, Math.round((pricingPreview.total - chosenCredit) * 100) / 100);
  const elearningInsufficient = isElearningOrder && creditBalance + 1e-6 < pricingPreview.total;

  const toggleUseCredit = (v) => {
    setUseCredit(v);
    if (v) {
      setCreditAmount(applicableCredit.toFixed(2));
    } else {
      setCreditAmount(0);
    }
  };

  const goToParticipants = () => {
    // Initialize slots arrays based on current quantities
    const next = {};
    items.forEach((it) => {
      const existing = slots[it.course_id] || [];
      const arr = [];
      for (let i = 0; i < it.quantity; i++) {
        arr.push(existing[i] || emptyParticipant());
      }
      next[it.course_id] = arr;
    });
    setSlots(next);
    setAddons((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => {
      const it = items.find((x) => k.startsWith(`${x.course_id}-`));
      return it && Number(k.slice(it.course_id.length + 1)) < it.quantity;
    })));
    setStep(1);
  };

  const updateSlot = (course_id, idx, field, value) => {
    setSlots((prev) => {
      const arr = [...(prev[course_id] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, [course_id]: arr };
    });
  };

  const validateParticipants = () => {
    for (const it of items) {
      const arr = slots[it.course_id] || [];
      for (let i = 0; i < arr.length; i++) {
        const p = arr[i];
        if (!p.first_name?.trim() || !p.last_name?.trim() || !p.email?.trim()
            || !p.fiscal_code?.trim() || !p.birth_date?.trim() || !p.birth_place?.trim()) {
          return `Corsista ${i + 1} di "${it.title}": nome, cognome, email, codice fiscale, data e luogo di nascita sono obbligatori.`;
        }
        if (p.requires_cfp) {
          if (!p.professional_profile?.trim()) {
            return `Corsista ${i + 1} di "${it.title}": CFP richiesti, seleziona il Profilo professionale.`;
          }
          if (!PROFESSIONAL_PROFILES.includes(p.professional_profile)) {
            return `Corsista ${i + 1} di "${it.title}": profilo professionale non valido.`;
          }
          if (!p.professional_province?.trim()) {
            return `Corsista ${i + 1} di "${it.title}": CFP richiesti, inserisci la Provincia dell'ordine professionale.`;
          }
        }
      }
    }
    return null;
  };

  const submitOrder = async () => {
    const err = validateParticipants();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const enrollments = [];
      items.forEach((it) => {
        (slots[it.course_id] || []).forEach((p, idx) => {
          enrollments.push({ course_id: it.course_id, edition_id: it.edition_id || null, ...p });
          const sp = addons[`${it.course_id}-${idx}`];
          if (sp && priceMap[sp]) {
            const cfp = !!priceMap[sp].cfp_enabled && p.requires_cfp;
            enrollments.push({ ...p, course_id: sp, edition_id: null, requires_cfp: cfp,
              professional_profile: cfp ? p.professional_profile : "", professional_province: cfp ? p.professional_province : "" });
          }
        });
      });
      const creditReq = isElearningOrder ? pricingPreview.total : 0;
      const payload = {
        items: effItems.map((i) => ({ course_id: i.course_id, edition_id: i.edition_id || null, quantity: i.quantity, unit_price: i.unit_price })),
        enrollments,
        notes,
        credit_amount_requested: creditReq,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      if (isElearningOrder && data.payment_status === "pagato") {
        toast.success(`Ordine ${data.order_number} confermato · pagato con Credito Partner`);
      } else {
        toast.success(`Ordine ${data.order_number} creato · Procedi al pagamento`);
      }
      navigate(`/partner/ordini/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nella creazione dell'ordine");
    } finally { setSubmitting(false); }
  };

  if (items.length === 0) {
    return (
      <div className="space-y-6" data-testid="cart-page">
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Carrello</h1>
        <Card className="border-slate-200"><CardContent className="p-10 text-center">
          <ShoppingCart className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 text-slate-500">Il tuo carrello è vuoto.</div>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/partner/catalogo")} data-testid="cart-goto-catalog">Vai al catalogo</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cart-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Carrello</h1>
          <p className="text-slate-500 mt-1">Step {step + 1} di 2 · {step === 0 ? "Verifica articoli" : "Dati corsisti"}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${step >= 0 ? "bg-amber-500" : "bg-slate-300"}`} />
          <div className="h-px w-10 bg-slate-200" />
          <div className={`w-2 h-2 rounded-full ${step >= 1 ? "bg-amber-500" : "bg-slate-300"}`} />
        </div>
      </div>

      {step === 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {items.map((it) => {
              const pr = priceOf(it);
              const unitFinal = pr.final;
              const hasDisc = unitFinal < pr.list;
              return (
              <Card key={it.course_id} className="border-slate-200" data-testid={`cart-item-${it.course_id}`}>
                <CardContent className="p-4 flex gap-4 items-center">
                  <img src={it.image_url} alt="" className="w-20 h-16 object-cover rounded-md bg-slate-100" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{it.title}</div>
                    {it.edition_label && <div className="text-xs font-semibold text-sky-700" data-testid={`cart-item-edition-${it.course_id}`}>Edizione: {it.edition_label}</div>}
                    <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                      <span className="capitalize">{it.modality}</span>
                      {pr.std !== null && <span data-testid={`cart-item-standard-${it.course_id}`}>Standard <span className="line-through">{eur(pr.std)}</span></span>}
                      {hasDisc ? (
                        <>
                          <span className="line-through">{eur(pr.list)}</span>
                          <span className="font-semibold text-slate-900">{eur(unitFinal)}</span>
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`cart-item-discount-${it.course_id}`}>{pr.custom ? "Prezzo dedicato" : `-${pr.pct}%`}</Badge>
                        </>
                      ) : (
                        <span className="font-semibold text-slate-900">{eur(unitFinal)}{pr.custom ? " · prezzo dedicato" : ""}</span>
                      )}
                    </div>
                  </div>
                  <Input type="number" min={1} value={it.quantity}
                    onChange={(e) => updateQty(it.course_id, parseInt(e.target.value || "1"))}
                    className="w-20" data-testid={`cart-qty-${it.course_id}`} />
                  <div className="font-semibold text-slate-900 w-24 text-right" data-testid={`cart-line-total-${it.course_id}`}>{eur(unitFinal * it.quantity)}</div>
                  <Button variant="ghost" size="icon" onClick={() => removeItem(it.course_id)} data-testid={`cart-remove-${it.course_id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </CardContent>
              </Card>
            );})}
          </div>
          <Card className="border-slate-200 athena-shadow h-fit">
            <CardContent className="p-6">
              <h2 className="font-display text-xl font-bold">Riepilogo</h2>
              <div className="flex justify-between mt-4 text-sm text-slate-600"><span>Articoli</span><span>{items.length}</span></div>
              <div className="flex justify-between text-sm text-slate-600"><span>Corsisti totali</span><span className="font-semibold">{totalSlots}</span></div>
              {Object.keys(addonQty).length > 0 && (
                <div className="text-xs text-indigo-800 bg-indigo-50 rounded-lg p-2 space-y-0.5" data-testid="cart-addons-summary">
                  <div className="font-semibold">Formazioni specifiche abbinate ai corsisti (incluse nel totale):</div>
                  {Object.entries(addonQty).map(([cid, q]) => (
                    <div key={cid} className="flex justify-between gap-2"><span className="truncate">{priceMap[cid]?.title}</span><span className="whitespace-nowrap">× {q}</span></div>
                  ))}
                </div>
              )}
              <div className="flex justify-between text-sm text-slate-500"><span>Imponibile listino</span><span data-testid="cart-subtotal-original">{eur(pricingPreview.subtotalOriginal)}</span></div>
              {pricingPreview.discountAmount > 0 && (
                <div className="flex justify-between text-sm text-emerald-700 font-semibold" data-testid="cart-partner-discount">
                  <span>Sconto partner</span><span>-{eur(pricingPreview.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-slate-600"><span>Imponibile scontato</span><span data-testid="cart-subtotal">{eur(pricingPreview.subtotal)}</span></div>
              {pricingPreview.saving > 0 && (
                <div className="flex justify-between text-xs text-emerald-700" data-testid="cart-saving">
                  <span>Risparmio sul prezzo standard ({eur(pricingPreview.standardNet)})</span><span>{eur(pricingPreview.saving)}</span>
                </div>
              )}
              {pricingPreview.vatExempt ? (
                <div className="flex justify-between text-xs text-blue-700" data-testid="cart-vat-exempt">
                  <span>IVA non applicata (pagamento con Credito Partner)</span><span>{eur(0)}</span>
                </div>
              ) : pricingPreview.breakdown.map((b) => (
                <div key={b.rate} className="flex justify-between text-xs text-slate-500" data-testid={`cart-vat-${b.rate}`}>
                  <span>IVA {b.rate}% su {eur(b.net)}</span><span>{eur(b.vat)}</span>
                </div>
              ))}
              <div className="border-t my-3" />
              <div className="flex justify-between font-display font-bold text-lg text-slate-900"><span>Totale stimato</span><span data-testid="cart-total">{eur(pricingPreview.total)}</span></div>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note per l'ordine (opzionale)" className="mt-4" data-testid="cart-notes" />
              <Button onClick={goToParticipants} className="w-full mt-4 h-11 bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="cart-next-step">
                Procedi · Inserisci corsisti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 flex gap-2">
                <Users className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Per ogni corso devi inserire i dati del corsista. Non puoi iscrivere più corsisti rispetto alle quantità acquistate.</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-6">
          {isElearningOrder && (
            <Card className={`border ${elearningInsufficient ? "border-red-300 bg-red-50/40" : "border-blue-200 bg-blue-50/40"}`} data-testid="checkout-elearning-credit">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="w-9 h-9 rounded-xl bg-white border border-blue-200 flex items-center justify-center">
                    <Wallet className="w-4 h-4 text-blue-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 flex items-center gap-2">
                      Corsi E-learning · pagamento solo con Credito Partner
                      <ModalityBadge modality="elearning" />
                    </div>
                    <div className="text-xs text-slate-500">
                      Credito disponibile: <strong>{eur(creditBalance)}</strong>{creditInitial > 0 ? ` su ${eur(creditInitial)}` : ""} · Importo da scalare (solo imponibile, IVA non applicata): <strong>{eur(pricingPreview.total)}</strong>
                    </div>
                  </div>
                </div>
                {elearningInsufficient && (
                  <div className="mt-3 text-sm text-red-700 flex items-center gap-2" data-testid="elearning-credit-insufficient">
                    <AlertTriangle className="w-4 h-4" /> Credito Partner insufficiente. Ricaricare il credito per procedere con l'acquisto.
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          {isClassroomOrder && (
            <Card className="border-slate-200 bg-slate-50/60" data-testid="checkout-classroom-info">
              <CardContent className="p-4 text-sm text-slate-700 flex items-center gap-3 flex-wrap">
                <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-slate-700" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Pagamento: PayPal o Bonifico Bancario</div>
                  <div className="text-xs text-slate-500">Per i corsi in Videoconferenza e Aula il Credito Partner non è disponibile.</div>
                </div>
              </CardContent>
            </Card>
          )}
          {items.map((it) => (
            <Card key={it.course_id} className="border-slate-200" data-testid={`checkout-course-${it.course_id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-display font-bold text-lg text-slate-900">{it.title}</div>
                    {it.edition_label && <div className="text-xs font-semibold text-sky-700" data-testid={`checkout-course-edition-${it.course_id}`}>Edizione: {it.edition_label}</div>}
                    <div className="text-xs text-slate-500" data-testid={`checkout-course-price-${it.course_id}`}>{it.quantity} corsista/i da inserire · {(() => { const pr = priceOf(it); const sv = pr.std !== null ? Math.max(0, pr.std - pr.final) : 0; return <>Prezzo standard {pr.std !== null ? eur(pr.std) : "—"} · {pr.custom ? "Sconto: prezzo dedicato · " : pr.pct > 0 ? `Sconto ${pr.pct}% · ` : ""}{sv > 0 ? `Risparmio −${eur(sv)} · ` : ""}<strong className="text-slate-900">Prezzo partner {eur(pr.final)} cad.</strong></>; })()}</div>
                  </div>
                  <div className="status-pill status-iscritto"><Users className="w-3 h-3" />{it.quantity} slot</div>
                </div>
                <div className="mt-5 space-y-5">
                  {(slots[it.course_id] || []).map((p, idx) => (
                    <div key={idx} className="p-4 rounded-xl border border-slate-200 bg-slate-50/60" data-testid={`checkout-slot-${it.course_id}-${idx}`}>
                      <div className="font-semibold text-slate-700 mb-3 text-sm">Corsista {idx + 1} di {it.quantity}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                          ["first_name", "Nome *", "text"],
                          ["last_name", "Cognome *", "text"],
                          ["fiscal_code", "Codice Fiscale *", "text"],
                          ["birth_date", "Data di nascita *", "date"],
                          ["birth_place", "Luogo di nascita *", "text"],
                          ["email", "Email *", "email"],
                          ["phone", "Telefono", "tel"],
                          ["company", "Azienda", "text"],
                          ["role", "Mansione", "text"],
                        ].map(([k, l, t]) => (
                          <div key={k}>
                            <Label className="text-xs">{l}</Label>
                            <Input type={t} value={p[k] || ""}
                              onChange={(e) => updateSlot(it.course_id, idx, k, e.target.value)}
                              data-testid={`checkout-input-${it.course_id}-${idx}-${k}`} />
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 p-3 rounded-lg border border-amber-200 bg-amber-50/60">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2 text-sm text-slate-800">
                            <GraduationCap className="w-4 h-4 text-amber-600" />
                            <span className="font-semibold">Richiede CFP (Crediti Formativi Professionali)</span>
                          </div>
                          <Switch
                            checked={!!p.requires_cfp}
                            disabled={!it.cfp_enabled}
                            onCheckedChange={(v) => updateSlot(it.course_id, idx, "requires_cfp", !!v)}
                            data-testid={`checkout-cfp-switch-${it.course_id}-${idx}`}
                          />
                        </div>
                        {!it.cfp_enabled && (
                          <div className="text-xs text-slate-500 mt-2">Questo corso non prevede il rilascio di CFP.</div>
                        )}
                        {p.requires_cfp && it.cfp_enabled && (
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Profilo professionale *</Label>
                              <Select
                                value={p.professional_profile || ""}
                                onValueChange={(v) => updateSlot(it.course_id, idx, "professional_profile", v)}
                              >
                                <SelectTrigger data-testid={`checkout-cfp-profile-${it.course_id}-${idx}`}>
                                  <SelectValue placeholder="Seleziona profilo" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(it.cfp_profiles || []).map((pp) => (
                                    <SelectItem key={pp} value={pp}>{pp}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Provincia *</Label>
                              <Input
                                value={p.professional_province || ""}
                                onChange={(e) => updateSlot(it.course_id, idx, "professional_province", e.target.value)}
                                placeholder="Es. Roma, Milano, RM, MI…"
                                data-testid={`checkout-cfp-province-${it.course_id}-${idx}`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {isGeneralCourse(it.title) && specificCourses.length > 0 && (
                        <div className="mt-4 p-3 rounded-lg border border-indigo-200 bg-indigo-50/60 space-y-2" data-testid={`checkout-specific-${it.course_id}-${idx}`}>
                          <Label className="text-sm font-semibold text-indigo-900">Aggiungi formazione specifica (facoltativo)</Label>
                          <Select value={addons[`${it.course_id}-${idx}`] || "none"}
                            onValueChange={(v) => setAddons((prev) => ({ ...prev, [`${it.course_id}-${idx}`]: v === "none" ? "" : v }))}>
                            <SelectTrigger className="bg-white" data-testid={`checkout-specific-select-${it.course_id}-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nessuna formazione specifica</SelectItem>
                              {specificCourses.map((s) => <SelectItem key={s.id} value={s.id}>{s.title} · {eur(s.partner_price_final ?? s.partner_price ?? 0)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-slate-500">Il corso scelto viene aggiunto all'ordine con gli stessi dati del corsista.</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          <Card className="border-slate-200 athena-shadow sticky bottom-4">
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <Button variant="outline" onClick={() => setStep(0)} data-testid="checkout-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
              </Button>
              <div className="flex-1 min-w-[160px] text-sm text-slate-600 text-right">
                <div className="font-display font-bold text-lg text-slate-900">Totale {eur(pricingPreview.total)}</div>
                <div className="text-xs">Imp. {eur(pricingPreview.subtotal)} · IVA {pricingPreview.vatExempt ? "non applicata" : eur(pricingPreview.vat)}{useCredit && chosenCredit > 0 ? ` · Credito -${eur(chosenCredit)}` : ""}</div>
              </div>
              <Button onClick={submitOrder} disabled={submitting || elearningInsufficient} className="h-11 bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60" data-testid="checkout-submit">
                <CheckCircle2 className="w-4 h-4 mr-1" /> {isElearningOrder ? "Conferma e paga con Credito" : "Conferma ordine"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
